import { useNode } from "@craftjs/core"
import { cloneDeep, get, set, unset } from "lodash"
import { X } from "lucide-react"
import { DefaultTooltip } from "twyne-new.ui"

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { removeEmptyObjects, validValue } from "./hooks"

const ClearProp = ({ keys }) => {
  const { device } = useResponsive()
  const { utilitiesParsed, setSelectedUtilities } = useUtilities()
  const { actions: { setProp }, props } = useNode(({ data: { props: { properties } } }) => ({ props: properties }))

  const handleClear = () => {
    const reseted = resetSingleProp({ props, keys, device, utilitiesParsed: !utilitiesParsed ? undefined : utilitiesParsed })
    setProp(props => set(props, 'properties', reseted))
    setSelectedUtilities(prev => [...prev])
  }

  return <>
    <DefaultTooltip content='Clear property' triggerProps={{ asChild: true }}>
      <X size='12' onClick={handleClear} />
    </DefaultTooltip>
  </>
}

export function resetSingleProp({ props, keys, device, utilitiesParsed = 'default', defaultProps = {} }) {
  const scenarios = keys.map(key => [
    { type: 'raw', key },
    { type: 'utility', key: `${key}.${utilitiesParsed}` },
    { type: 'utilityDefault', key: `${key}.${utilitiesParsed}.default` },
    { type: 'device', key: `${key}.${device}` },
    { type: 'utilityDevice', key: `${key}.${device}.${utilitiesParsed}` },
  ]).flat()

  const response = cloneDeep(props)

  for (const { type, key } of scenarios) {
    const keyValue = get(response, key)
    if (validValue(keyValue)) {
      if (type === 'raw' && device === 'mobile') {
        continue
      }

      if (type === 'raw' && !get(defaultProps, key)) {
        unset(response, key)
        continue
      }

      set(response, key, get(defaultProps, key) || null)
    }
  }

  return cleanStructure({ object: response, scenarios, utilitiesParsed, keys })
}

function cleanStructure({ object, scenarios, utilitiesParsed, keys }) {
  const utilities = utilitiesParsed === 'default' ? [] : utilitiesParsed.split('.')
  for (const key of keys) {
    for (let i = utilities.length; i > 0; i--) {
      const nested = utilities.slice(0, i).join('.')
      scenarios.push({ key: `${key}.${nested}` })
    }
  }

  const response = cloneDeep(object)

  for (const { key } of scenarios) {
    const value = get(response, key)

    if (value && typeof value === 'object') {
      const { default: defaultValue, desktop, mobile, ...rest } = value

      const hasUtilitiesValue = Object.values(rest).some(item => item !== null)
      const onlyDefaultValue = !desktop && !mobile && defaultValue !== null && !hasUtilitiesValue
      if (onlyDefaultValue) {
        set(response, key, defaultValue)
        continue
      }

      const onlyDesktopValue = desktop !== null && mobile === null
      if (onlyDesktopValue) {
        set(response, key, desktop)
        continue
      }

      const deviceEmptyValues = desktop === null && mobile === null && !hasUtilitiesValue
      if (deviceEmptyValues) {
        unset(response, key)
        continue
      }
    }
  }

  return removeEmptyObjects(response)
}

export default ClearProp
